import { useMemo } from "react";
import { useQuery } from "@rocicorp/zero/react";
import { queries } from "@/lib/queries";
import {
  useActiveUserMap,
  useLastMessageAtMap,
  useMyMemberships,
  useSharedWorkspaceUserIdSet,
} from "@/lib/selectors";
import {
  buildDirectDmRows,
  filterDmDisplay,
  filterGroupDmDisplay,
  findSelfDmRow,
  type DmRow,
} from "./dm-display-filters";

interface UseDmListDataArgs {
  workspaceId: string | null;
  currentUserId: string;
  unreadOnly: boolean | undefined;
  recentOnly: boolean | undefined;
  isUnread: ((channelId: string) => boolean) | undefined;
}

/**
 * Single hook that owns every Zero query + derivation `DMList` needs.
 *
 * Returns three buckets:
 *
 *   • Raw row sets (`visibleDirectDms`, `visibleGroupDms`, `members`)
 *     after hidden-channel + deactivated-user filtering.
 *
 *   • Lookup maps the parent threads through to row leaves
 *     (`dmByUserId`, `collaboratorIdSet`, `wsMemberIdSet`,
 *     `inviteByChannelId`, `lastMessageAtByChannelId`,
 *     `membershipByChannelId`).
 *
 *   • Pre-grouped + display-filtered arrays the section render
 *     consumes directly — `displayWsDms`, `displayCollaboratorDms`,
 *     `displayWsGroupDms`, `displayGuestDms`, plus the four Home-view
 *     equivalents (`displayHomeSharedDms`, `displayHomePersonalDms`,
 *     `displayHomeSharedGroupDms`, `displayHomePersonalGroupDms`).
 *
 * Lifted out of `DMList.tsx` so the parent stays under the file
 * ceiling and the data shape can be reasoned about in isolation.
 */
export function useDmListData({
  workspaceId,
  currentUserId,
  unreadOnly,
  recentOnly,
  isUnread,
}: UseDmListDataArgs) {
  const [activeUserMap] = useActiveUserMap();
  const [lastMessageAtByChannelId] = useLastMessageAtMap();
  const [myMemberships] = useMyMemberships();
  const [sharedWsUserIdSet] = useSharedWorkspaceUserIdSet();

  // Workspace scope → server-scoped DMs (only DMs with workspace members).
  // Home scope → all DMs. Two queries because they have different arg types.
  const [workspaceScopedDms] = useQuery(
    workspaceId
      ? queries.channels.dmsByWorkspaceAndUserWithMembers({
          workspaceId,
          includeHidden: true,
        })
      : null,
  );
  // Always query the caller's full (workspace-independent) DM set. In Home
  // scope it feeds the buckets below; in every scope it's the source for the
  // pinned self-DM ("Notes"), which has no workspace and is otherwise filtered
  // out of the workspace-scoped query.
  // `includeHidden: true`: the sidebar must SEE hidden DMs so it can (a) keep
  // their peers off the workspace roster and (b) resurface them while unread.
  // Display filtering happens client-side via `visibleDirectDms` below.
  const [allDms] = useQuery(queries.channels.dmsByUserWithMembers(true));
  const channelsWithMembersRaw = workspaceId ? workspaceScopedDms : allDms;

  // The self-DM row, pinned at the top of the DM list in every scope.
  // Raw — hidden filtering is applied below once `hiddenChannelIds` exists
  // (the DM query now returns hidden rows, so an unfiltered self-DM would
  // keep showing "Notes" even after the user hid it).
  const selfDmRaw = useMemo(
    () =>
      findSelfDmRow({
        channels: allDms ?? [],
        currentUserId,
        getUser: (id) => activeUserMap.get(id),
        getLastMessageAt: (id) => lastMessageAtByChannelId.get(id) ?? 0,
      }),
    [allDms, currentUserId, activeUserMap, lastMessageAtByChannelId],
  );
  const channelsWithMembers = useMemo(
    () => channelsWithMembersRaw ?? [],
    [channelsWithMembersRaw],
  );
  const dmChannels = useMemo(
    () =>
      channelsWithMembers.filter(
        (ch) => ch.type === "dm" || ch.type === "group_dm",
      ),
    [channelsWithMembers],
  );

  const [wsMembers] = useQuery(
    workspaceId ? queries.workspace_members.byWorkspace(workspaceId) : null,
  );
  const collaboratorIdSet = useMemo(
    () =>
      new Set(
        (wsMembers ?? [])
          .filter((m) => m.role === "collaborator")
          .map((m) => m.user_id),
      ),
    [wsMembers],
  );
  const wsMemberIds = useMemo(
    () => [
      ...new Set(
        (wsMembers ?? [])
          .filter((m) => m.role !== "collaborator")
          .map((m) => m.user_id),
      ),
    ],
    [wsMembers],
  );
  const wsMemberIdSet = useMemo(() => new Set(wsMemberIds), [wsMemberIds]);

  const directDmChannels = useMemo(
    () => dmChannels.filter((ch) => ch.type === "dm"),
    [dmChannels],
  );

  const existingDirectDms = useMemo<DmRow[]>(
    () =>
      buildDirectDmRows({
        channels: directDmChannels,
        currentUserId,
        getUser: (id) => activeUserMap.get(id),
        getLastMessageAt: (id) => lastMessageAtByChannelId.get(id) ?? 0,
      }),
    [currentUserId, directDmChannels, lastMessageAtByChannelId, activeUserMap],
  );

  const dmByUserId = useMemo(() => {
    const map = new Map<string, string>();
    for (const dm of existingDirectDms) {
      if (!dm.userId) continue;
      map.set(dm.userId, dm.channelId);
    }
    return map;
  }, [existingDirectDms]);

  const membershipByChannelId = useMemo(() => {
    const map = new Map<string, { id: string; hidden: boolean }>();
    for (const m of myMemberships) {
      map.set(m.channel_id, { id: m.id, hidden: Boolean(m.hidden) });
    }
    return map;
  }, [myMemberships]);

  const hiddenChannelIds = useMemo(() => {
    const set = new Set<string>();
    for (const [channelId, membership] of membershipByChannelId) {
      if (membership.hidden) set.add(channelId);
    }
    return set;
  }, [membershipByChannelId]);

  // Drop the pinned self-DM ("Notes") when the caller has hidden it — unless
  // it's unread (mirrors the `visibleDirectDms` rule so a hidden Notes can
  // still resurface on new activity).
  const selfDm = useMemo(
    () =>
      selfDmRaw &&
      (!hiddenChannelIds.has(selfDmRaw.channelId) ||
        isUnread?.(selfDmRaw.channelId))
        ? selfDmRaw
        : undefined,
    [selfDmRaw, hiddenChannelIds, isUnread],
  );

  const visibleDirectDms = useMemo(
    () =>
      existingDirectDms.filter(
        (dm) => !hiddenChannelIds.has(dm.channelId) || isUnread?.(dm.channelId),
      ),
    [existingDirectDms, hiddenChannelIds, isUnread],
  );

  // Workspace members excluding self and system-bot, sorted: online first.
  // Deactivated members are hidden so the sidebar doesn't surface
  // "Deactivated User" / orphaned-account rows under "Ano members".
  const members = useMemo(
    () =>
      wsMemberIds
        .filter((id) => id !== currentUserId && id !== "system-bot")
        .map((id) => activeUserMap.get(id))
        .filter(
          (u): u is NonNullable<typeof u> => Boolean(u) && !u!.is_deactivated,
        )
        .filter((u) => !dmByUserId.has(u.id))
        .sort((a, b) => a.display_name.localeCompare(b.display_name)),
    [currentUserId, activeUserMap, wsMemberIds, dmByUserId],
  );

  const guestDms = useMemo(
    () => visibleDirectDms.filter((dm) => dm.isGuest),
    [visibleDirectDms],
  );

  // Invite lookup for guest DM badges (only query when guest DMs exist)
  const [myInvitesRaw] = useQuery(
    guestDms.length > 0 ? queries.invites.byCreator() : null,
  );
  const inviteByChannelId = useMemo(() => {
    const map = new Map<
      string,
      { invited_email?: string | null; type: string }
    >();
    for (const inv of myInvitesRaw ?? []) {
      if (inv.channel_id && inv.type === "dm") {
        map.set(inv.channel_id, inv);
      }
    }
    return map;
  }, [myInvitesRaw]);

  const workspaceDms = useMemo(
    () =>
      visibleDirectDms.filter(
        (dm) => dm.userId && wsMemberIdSet.has(dm.userId) && !dm.isGuest,
      ),
    [visibleDirectDms, wsMemberIdSet],
  );
  const collaboratorDms = useMemo(
    () =>
      visibleDirectDms.filter(
        (dm) => dm.userId && collaboratorIdSet.has(dm.userId) && !dm.isGuest,
      ),
    [visibleDirectDms, collaboratorIdSet],
  );
  const groupDms = useMemo(
    () => dmChannels.filter((ch) => ch.type === "group_dm"),
    [dmChannels],
  );
  const visibleGroupDms = useMemo(
    () =>
      groupDms.filter(
        (ch) => !hiddenChannelIds.has(ch.id) || isUnread?.(ch.id),
      ),
    [groupDms, hiddenChannelIds, isUnread],
  );
  const workspaceGroupDms = useMemo(
    () =>
      visibleGroupDms.filter((ch) =>
        ch.members?.some(
          (m) => m.user_id !== currentUserId && wsMemberIdSet.has(m.user_id),
        ),
      ),
    [visibleGroupDms, currentUserId, wsMemberIdSet],
  );

  const displayWsDms = useMemo(
    () =>
      filterDmDisplay(
        workspaceDms,
        unreadOnly,
        recentOnly,
        isUnread,
        lastMessageAtByChannelId,
      ),
    [workspaceDms, unreadOnly, recentOnly, isUnread, lastMessageAtByChannelId],
  );
  const displayCollaboratorDms = useMemo(
    () =>
      filterDmDisplay(
        collaboratorDms,
        unreadOnly,
        recentOnly,
        isUnread,
        lastMessageAtByChannelId,
        5,
      ),
    [
      collaboratorDms,
      unreadOnly,
      recentOnly,
      isUnread,
      lastMessageAtByChannelId,
    ],
  );
  const displayWsGroupDms = useMemo(
    () =>
      filterGroupDmDisplay(
        workspaceGroupDms,
        unreadOnly,
        recentOnly,
        isUnread,
        lastMessageAtByChannelId,
      ),
    [
      workspaceGroupDms,
      unreadOnly,
      recentOnly,
      isUnread,
      lastMessageAtByChannelId,
    ],
  );
  const displayGuestDms = useMemo(() => {
    const withMessages = guestDms.filter(
      (dm) => lastMessageAtByChannelId.get(dm.channelId) != null,
    );
    return filterDmDisplay(
      withMessages,
      unreadOnly,
      recentOnly,
      isUnread,
      lastMessageAtByChannelId,
    );
  }, [guestDms, unreadOnly, recentOnly, isUnread, lastMessageAtByChannelId]);

  // Home view: split DMs into shared workspace vs personal
  const homeSharedDms = useMemo(() => {
    if (workspaceId) return [];
    return visibleDirectDms.filter(
      (dm) =>
        !dm.isGuest &&
        dm.userId &&
        dm.userId !== currentUserId &&
        sharedWsUserIdSet.has(dm.userId),
    );
  }, [workspaceId, visibleDirectDms, sharedWsUserIdSet, currentUserId]);

  const homePersonalDms = useMemo(() => {
    if (workspaceId) return [];
    return visibleDirectDms.filter(
      (dm) =>
        !dm.isGuest &&
        (!dm.userId ||
          (dm.userId !== currentUserId && !sharedWsUserIdSet.has(dm.userId))),
    );
  }, [workspaceId, visibleDirectDms, sharedWsUserIdSet, currentUserId]);

  const homeSharedGroupDms = useMemo(() => {
    if (workspaceId) return [];
    return visibleGroupDms.filter((ch) =>
      ch.members?.some(
        (m) => m.user_id !== currentUserId && sharedWsUserIdSet.has(m.user_id),
      ),
    );
  }, [workspaceId, visibleGroupDms, currentUserId, sharedWsUserIdSet]);

  const homePersonalGroupDms = useMemo(() => {
    if (workspaceId) return [];
    return visibleGroupDms.filter(
      (ch) =>
        !ch.members?.some(
          (m) =>
            m.user_id !== currentUserId && sharedWsUserIdSet.has(m.user_id),
        ),
    );
  }, [workspaceId, visibleGroupDms, currentUserId, sharedWsUserIdSet]);

  const displayHomeSharedDms = useMemo(
    () =>
      filterDmDisplay(
        homeSharedDms,
        unreadOnly,
        recentOnly,
        isUnread,
        lastMessageAtByChannelId,
      ),
    [homeSharedDms, unreadOnly, recentOnly, isUnread, lastMessageAtByChannelId],
  );
  const displayHomePersonalDms = useMemo(
    () =>
      filterDmDisplay(
        homePersonalDms,
        unreadOnly,
        recentOnly,
        isUnread,
        lastMessageAtByChannelId,
      ),
    [
      homePersonalDms,
      unreadOnly,
      recentOnly,
      isUnread,
      lastMessageAtByChannelId,
    ],
  );
  const displayHomeSharedGroupDms = useMemo(
    () =>
      filterGroupDmDisplay(
        homeSharedGroupDms,
        unreadOnly,
        recentOnly,
        isUnread,
        lastMessageAtByChannelId,
      ),
    [
      homeSharedGroupDms,
      unreadOnly,
      recentOnly,
      isUnread,
      lastMessageAtByChannelId,
    ],
  );
  const displayHomePersonalGroupDms = useMemo(
    () =>
      filterGroupDmDisplay(
        homePersonalGroupDms,
        unreadOnly,
        recentOnly,
        isUnread,
        lastMessageAtByChannelId,
      ),
    [
      homePersonalGroupDms,
      unreadOnly,
      recentOnly,
      isUnread,
      lastMessageAtByChannelId,
    ],
  );

  const visibleMembersList = useMemo(
    () => (unreadOnly || recentOnly ? [] : members),
    [unreadOnly, recentOnly, members],
  );

  return {
    selfDm,
    visibleDirectDms,
    visibleGroupDms,
    members,
    visibleMembersList,
    dmByUserId,
    collaboratorIdSet,
    membershipByChannelId,
    lastMessageAtByChannelId,
    inviteByChannelId,
    displayWsDms,
    displayCollaboratorDms,
    displayWsGroupDms,
    displayGuestDms,
    displayHomeSharedDms,
    displayHomePersonalDms,
    displayHomeSharedGroupDms,
    displayHomePersonalGroupDms,
  };
}
